"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";
import { CIRCLE_ACTION_VARIANT_STYLES, type CircleActionVariant } from "./circle-action-variants";
import { Check } from "lucide-react";

export type GlassDropdownSize = "xs" | "sm" | "md";

export interface GlassDropdownItem {
  value: string;
  label: string;
  icon: React.ElementType;
  variant?: CircleActionVariant;
  disabled?: boolean;
}

export interface GlassDropdownProps {
  trigger: React.ReactNode;
  items: GlassDropdownItem[];
  value?: string;
  onSelect: (value: string) => void;
  onHoverChange?: (value: string | null) => void;
  disabled?: boolean;
  size?: GlassDropdownSize;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  sideOffset?: number;
  className?: never;
}

const SIZE = {
  xs: {
    panel:    "p-[3px] min-w-[144px]",
    item:     "gap-1.5 rounded-[8px] px-1.5 py-[4px] text-[11px]",
    iconWrap: "h-[16px] w-[16px] rounded-[5px]",
    iconSize: 10,
    check:    9,
  },
  sm: {
    panel:    "p-1 min-w-[160px]",
    item:     "gap-1.5 rounded-lg px-2 py-1.5 text-[11px]",
    iconWrap: "h-[18px] w-[18px] rounded-md",
    iconSize: 11,
    check:    10,
  },
  md: {
    panel:    "p-1.5 min-w-[188px]",
    item:     "gap-2 rounded-xl px-2.5 py-[7px] text-[12px]",
    iconWrap: "h-[20px] w-[20px] rounded-lg",
    iconSize: 12,
    check:    11,
  },
} satisfies Record<GlassDropdownSize, {
  panel: string; item: string; iconWrap: string; iconSize: number; check: number;
}>;

export function GlassDropdown({
  trigger,
  items,
  value = "",
  onSelect,
  onHoverChange,
  disabled = false,
  size = "md",
  align = "end",
  side = "bottom",
  sideOffset = 6,
  className,
}: GlassDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
  const s = SIZE[size];

  function handleOpenChange(next: boolean) {
    if (!next) { setHoveredItem(null); onHoverChange?.(null); }
    setOpen(next);
  }

  function handleItemHover(val: string | null) {
    setHoveredItem(val);
    onHoverChange?.(val);
  }

  function handleSelect(itemValue: string) {
    onSelect(itemValue);
    setOpen(false);
    setHoveredItem(null);
    onHoverChange?.(null);
  }

  return (
    <>
      <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild disabled={disabled}>
          <span className="inline-flex">{trigger}</span>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align={align}
            side={side}
            sideOffset={sideOffset}
            onMouseLeave={() => handleItemHover(null)}
            onPointerLeave={() => handleItemHover(null)}
            className={cn(
              "gd-panel rounded-2xl",
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
              "duration-150",
              s.panel,
              className,
            )}
            style={{ zIndex: Z_INDEX.DROPDOWN }}
          >
            {items.map((item) => {
              const isSelected = !!value && value.toUpperCase() === item.value.toUpperCase();
              const isHovered  = !!hoveredItem && hoveredItem.toUpperCase() === item.value.toUpperCase() && !isSelected;
              const vs  = item.variant ? CIRCLE_ACTION_VARIANT_STYLES[item.variant] : null;
              const acc = vs?.accentColor ?? "rgb(100 116 139)";
              const rgb = vs?.shadowRgb;

              const selStyle: React.CSSProperties = isSelected && rgb
                ? {
                    background: [
                      "linear-gradient(to bottom, rgba(255,255,255,0.68) 0%, rgba(255,255,255,0.22) 100%)",
                      "radial-gradient(ellipse 38% 30% at 16% 10%, rgba(255,255,255,0.40) 0%, transparent 70%)",
                      `rgba(${rgb}, 0.12)`,
                    ].join(", "),
                    boxShadow: [
                      "inset 0 1px 0 rgba(255,255,255,0.82)",
                      "inset 0 0 0 0.5px rgba(255,255,255,0.56)",
                      `inset 0 0 0 1px rgba(${rgb}, 0.20)`,
                      `0 1px 3px rgba(${rgb}, 0.08)`,
                    ].join(", "),
                    color: acc,
                  }
                : {};

              const hovStyle: React.CSSProperties = isHovered
                ? {
                    background: [
                      "linear-gradient(to bottom, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.26) 100%)",
                      "rgba(255,255,255,0.40)",
                    ].join(", "),
                    boxShadow: [
                      "inset 0 1px 0 rgba(255,255,255,0.88)",
                      "inset 0 0 0 0.5px rgba(255,255,255,0.58)",
                    ].join(", "),
                  }
                : {};

              return (
                <button
                  key={item.value}
                  role="option"
                  type="button"
                  aria-selected={isSelected}
                  disabled={item.disabled}
                  onClick={() => handleSelect(item.value)}
                  onMouseEnter={() => handleItemHover(item.value)}
                  onPointerEnter={() => handleItemHover(item.value)}
                  onMouseLeave={() => handleItemHover(null)}
                  onPointerLeave={() => handleItemHover(null)}
                  className={cn(
                    "gd-item relative flex w-full items-center tracking-[-0.01em]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/50",
                    "disabled:pointer-events-none disabled:opacity-40",
                    isSelected ? "font-semibold" : "font-medium",
                    s.item,
                  )}
                  style={{
                    ...(isSelected ? selStyle : isHovered ? hovStyle : {}),
                    color: isSelected ? acc : isHovered ? "rgb(30 41 59)" : undefined,
                    transition: "background 120ms ease, box-shadow 120ms ease, color 100ms ease",
                  }}
                >
                  <span
                    className={cn("flex flex-shrink-0 items-center justify-center", s.iconWrap)}
                    style={{
                      ...(isSelected && rgb ? {
                        background: [
                          "linear-gradient(to bottom, rgba(255,255,255,0.60) 0%, rgba(255,255,255,0.18) 100%)",
                          `rgba(${rgb}, 0.12)`,
                        ].join(", "),
                      } : {}),
                      color: isSelected || isHovered ? acc : "rgb(148 163 184)",
                    }}
                  >
                    {React.createElement(item.icon, { size: s.iconSize, strokeWidth: 1.75 })}
                  </span>

                  <span className="flex-1 text-left">{item.label}</span>

                  {isSelected && (
                    <Check className="h-4 w-4" size={s.check} strokeWidth={2.5} style={{ color: acc, flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      <style jsx global>{`
        /* ── Panel: glass equilibrado — blanco legible con transparencia suave */
        .gd-panel {
          isolation: isolate;
          overflow: hidden;
          background:
            linear-gradient(to bottom, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.76) 55%, rgba(255,255,255,0.62) 100%),
            radial-gradient(ellipse 65% 45% at 16% 8%, rgba(255,255,255,0.68) 0%, transparent 65%),
            radial-gradient(ellipse 35% 25% at 88% 90%, rgba(226,232,240,0.18) 0%, transparent 60%),
            rgba(248,250,252,0.78);
          backdrop-filter: blur(20px) saturate(1.28);
          -webkit-backdrop-filter: blur(20px) saturate(1.28);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.78),
            inset -1px  0   0 rgba(255,255,255,0.30),
            inset  0   -1px 0 rgba(255,255,255,0.42),
            inset  0    0   0 1px rgba(255,255,255,0.52),
            inset  1px  1px 0 rgba(15,23,42,0.05),
            0 2px 6px rgba(15,23,42,0.05),
            0 8px 24px -6px rgba(15,23,42,0.10),
            0 20px 48px -16px rgba(15,23,42,0.12);
        }
        .dark .gd-panel {
          background:
            linear-gradient(to bottom, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%),
            radial-gradient(ellipse 55% 35% at 18% 12%, rgba(255,255,255,0.10) 0%, transparent 65%),
            rgba(20,24,40,0.86);
          backdrop-filter: blur(20px) saturate(1.22) brightness(0.95);
          -webkit-backdrop-filter: blur(20px) saturate(1.22) brightness(0.95);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.24),
            inset -1px  0   0 rgba(255,255,255,0.10),
            inset  0   -1px 0 rgba(255,255,255,0.16),
            inset  0    0   0 1px rgba(255,255,255,0.14),
            inset  1px  1px 0 rgba(0,0,0,0.22),
            0 4px 8px rgba(0,0,0,0.22),
            0 20px 48px -16px rgba(0,0,0,0.48);
        }

        /* ── Items: default text colour ─────────────────────────────────────── */
        .gd-item { color: rgb(51 65 85); }
        .dark .gd-item { color: rgb(203 213 225); }
      `}</style>
    </>
  );
}
